/**
 * Arrays of objects → TableBlock (spec §9): column roles, rows, grouping,
 * totals. `spec` is a config table section (all optional).
 */
import { isPlainObject } from "../normalizeExtract";
import { displayCase, humanize, pluralize, sentenceCase } from "../format/labels";
import {
  detectFormat,
  enumTone,
  fmtMoney,
  fmtNumber,
  formatScalar,
  isEmptyValue,
  toNumber,
} from "../format/registry";

const TITLE_KEYS = [
  "name", "itemname", "item_name", "section_name", "title", "label", "creditor",
  "payee", "merchant", "account", "item", "description",
];
const SUBTITLE_KEYS = ["description", "summary", "summary_of_section", "notes", "memo", "detail"];
const VALUE_HINT = /balance|amount|total|cost|value|price/i;
const QTY_KEY = /^(quantity|qty)|squarefootage|^units$|count$/i;

const majority = (arr) => {
  const counts = new Map();
  arr.forEach((x) => counts.set(x, (counts.get(x) || 0) + 1));
  let best = null;
  let n = -1;
  counts.forEach((c, k) => {
    if (c > n) {
      best = k;
      n = c;
    }
  });
  return best;
};

/** Column stats over up to 200 rows. */
export const detectColumns = (rows) => {
  const order = [];
  const stats = new Map();
  rows.slice(0, 200).forEach((row) => {
    if (!isPlainObject(row)) return;
    Object.entries(row).forEach(([k, v]) => {
      if (!stats.has(k)) {
        order.push(k);
        stats.set(k, { formats: [], nonNull: 0, distinct: new Set(), maxLen: 0, nested: false });
      }
      const s = stats.get(k);
      if (isEmptyValue(v)) return;
      s.nonNull += 1;
      if (isPlainObject(v) || Array.isArray(v)) {
        s.nested = true;
        s.formats.push("json");
        return;
      }
      s.formats.push(detectFormat(k, v, row));
      s.distinct.add(String(v));
      s.maxLen = Math.max(s.maxLen, String(v).length);
    });
  });
  return order.map((key) => {
    const s = stats.get(key);
    return {
      key,
      label: humanize(key),
      format: s.formats.length ? majority(s.formats) : "text",
      nonNull: s.nonNull,
      distinct: s.distinct.size,
      maxLen: s.maxLen,
      nested: s.nested,
    };
  });
};

const findKey = (cols, candidates, exclude = []) => {
  for (const cand of candidates) {
    const c = cols.find(
      (col) =>
        !exclude.includes(col.key) &&
        (col.key.toLowerCase() === cand || col.key.toLowerCase().endsWith(cand)) &&
        !col.nested,
    );
    if (c) return c.key;
  }
  return undefined;
};

/** Qty / area / count tag text, or null. */
const quantityTag = (key, v) => {
  const n = toNumber(v);
  if (n === null) return null;
  const k = key.toLowerCase();
  if (/squarefootage/.test(k)) return `${fmtNumber(n)} sq ft`;
  if (/^(quantity|qty)/.test(k)) return `Qty ${fmtNumber(n)}`;
  if (k === "units") return pluralize(n, "unit");
  const base = humanize(key.replace(/_?count$/i, "").replace(/Count$/, "")).toLowerCase();
  return base ? `${fmtNumber(n)} ${base}` : `Count ${fmtNumber(n)}`;
};

/** Auto tags for a row from its tag columns. */
const autoTags = (row, tagCols) =>
  tagCols.flatMap((col) => {
    const v = row[col.key];
    if (isEmptyValue(v)) return [];
    if (col.format === "boolean") {
      const b = formatScalar(col.key, v).display === "Yes";
      return b ? [{ label: humanize(col.key, { booleanLike: true }), tone: "neutral" }] : [];
    }
    if (QTY_KEY.test(col.key)) {
      const t = quantityTag(col.key, v);
      return t ? [{ label: t, tone: "neutral" }] : [];
    }
    const tone = enumTone(v);
    return [{ label: sentenceCase(v), tone: tone === "ok" || tone === "warn" || tone === "crit" ? tone : "neutral" }];
  });

const sameLabel = (a, b) => {
  const x = String(a || "").toLowerCase().trim();
  const y = String(b || "").toLowerCase().trim();
  return !!x && !!y && (x === y || x.startsWith(y) || y.startsWith(x));
};

/**
 * @param {string} key array key
 * @param {object[]} arr
 * @param {string} path
 * @param {object} spec config table section (optional)
 * @param {object} ctx { payload, buildDetail(key, value, path, parentKey) => Block[] }
 */
export function buildTable(key, arr, path, spec = {}, ctx = {}) {
  const items = arr.map((r) => (isPlainObject(r) ? r : { value: r }));
  const cols = detectColumns(items).filter((c) => c.nonNull > 0);
  const byKey = Object.fromEntries(cols.map((c) => [c.key, c]));
  const stringCol = (c) => ["text", "enum", "prose", "id"].includes(c.format) && !c.nested;

  const titleKey =
    spec.titleKey ||
    findKey(cols, TITLE_KEYS) ||
    cols.find(stringCol)?.key;
  const subtitleKey =
    spec.subtitleKey !== undefined
      ? spec.subtitleKey
      : findKey(cols, SUBTITLE_KEYS, [titleKey]);
  const moneyCols = cols.filter((c) => c.format === "money");
  const valueKey =
    spec.valueKey ||
    moneyCols.find((c) => VALUE_HINT.test(c.key))?.key ||
    [...moneyCols].sort((a, b) => b.nonNull - a.nonNull)[0]?.key;

  const taken = new Set([titleKey, subtitleKey, valueKey].filter(Boolean));
  const tagCols = spec.tags
    ? []
    : cols.filter(
        (c) =>
          !taken.has(c.key) &&
          !c.nested &&
          ((c.format === "enum" && c.distinct <= 6) ||
            c.format === "boolean" ||
            QTY_KEY.test(c.key)),
      );
  tagCols.forEach((c) => taken.add(c.key));

  const rank = { date: 0, datetime: 0, money: 1, number: 2, percent: 2 };
  const visibleColumns = (
    spec.columns ||
    cols
      .filter((c) => !taken.has(c.key) && rank[c.format] !== undefined && !c.nested)
      .sort((a, b) => rank[a.format] - rank[b.format] || b.nonNull - a.nonNull)
      .slice(0, 2)
      .map((c) => c.key)
  ).filter((k) => byKey[k]);
  visibleColumns.forEach((k) => taken.add(k));

  const detailKeys = (spec.detail || [])
    .concat(cols.filter((c) => !taken.has(c.key)).map((c) => c.key))
    .filter((k, i, a) => a.indexOf(k) === i && byKey[k] && !visibleColumns.includes(k));

  const columns = cols.map((c) => ({
    key: c.key,
    label: c.label,
    format: c.format,
    nonNull: c.nonNull,
    role:
      c.key === titleKey
        ? "title"
        : c.key === subtitleKey
          ? "subtitle"
          : c.key === valueKey
            ? "value"
            : visibleColumns.includes(c.key)
              ? "column"
              : tagCols.some((t) => t.key === c.key)
                ? "tag"
                : "detail",
  }));

  // ── rows ──
  let rows = items.map((row, index) => {
    const rowPath = `${path}[${index}]`;
    const rawTitle = titleKey ? row[titleKey] : null;
    const title = isEmptyValue(rawTitle) ? `${humanize(key)} ${index + 1}` : displayCase(rawTitle);
    const rawSub = subtitleKey ? row[subtitleKey] : null;
    const value = valueKey ? toNumber(isPlainObject(row[valueKey]) ? row[valueKey].amount : row[valueKey]) : null;
    const cells = {};
    visibleColumns.forEach((k) => {
      const v = row[k];
      cells[k] = isEmptyValue(v)
        ? { display: null, format: byKey[k].format }
        : { display: formatScalar(k, v, { siblings: row }).display, format: byKey[k].format };
    });
    const tags = spec.tags ? spec.tags(row) || [] : autoTags(row, tagCols);
    const detail = detailKeys.flatMap((k) =>
      ctx.buildDetail ? ctx.buildDetail(k, row[k], `${rowPath}.${k}`, key, row) : [],
    );
    return {
      path: rowPath,
      index,
      raw: row,
      rawTitle: typeof rawTitle === "string" ? rawTitle : null,
      title,
      subtitle: isEmptyValue(rawSub) ? undefined : displayCase(rawSub),
      tags,
      cells,
      detail,
      value,
      valueDisplay: value === null ? spec.emptyValueLabel || "Not provided" : fmtMoney(value),
      share: null,
      empty: !!valueKey && value === null,
      muted: spec.rowMuted ? !!spec.rowMuted(row) : false,
      findQuery:
        typeof rawTitle === "string" && rawTitle.trim().length >= 3 ? rawTitle.trim() : undefined,
    };
  });

  // ── sort ──
  if (spec.sortBy) {
    const { key: sk, rank: order, dir = "asc" } = spec.sortBy;
    const pos = (r) => {
      const v = r.raw[sk];
      if (order) {
        const i = order.findIndex((o) => String(o).toLowerCase() === String(v ?? "").toLowerCase());
        return i < 0 ? order.length : i;
      }
      return toNumber(v) ?? String(v ?? "");
    };
    rows = rows
      .map((r, i) => ({ r, i }))
      .sort((a, b) => {
        const pa = pos(a.r);
        const pb = pos(b.r);
        const c = pa < pb ? -1 : pa > pb ? 1 : 0;
        return (dir === "desc" ? -c : c) || a.i - b.i;
      })
      .map((x) => x.r);
  }
  if (spec.rowMuted) rows = [...rows.filter((r) => !r.muted), ...rows.filter((r) => r.muted)];

  // ── totals + shares ──
  const withValue = rows.filter((r) => r.value !== null);
  const sum = withValue.reduce((n, r) => n + r.value, 0);
  if (valueKey && sum > 0) rows.forEach((r) => (r.share = r.value === null ? null : r.value / sum));
  let total;
  if (valueKey) {
    const expected = spec.expectedTotal ? toNumber(spec.expectedTotal(ctx.payload)) : null;
    total = {
      value: sum,
      display: fmtMoney(sum),
      expected: expected ?? undefined,
      expectedDisplay: expected !== null && expected !== undefined ? fmtMoney(expected) : undefined,
      matches: expected !== null && expected !== undefined ? Math.abs(expected - sum) < 0.5 : undefined,
    };
  }

  // ── grouping ──
  let groups;
  let groupKey = spec.groupBy;
  if (!groupKey && rows.length >= 8) {
    groupKey = cols.find(
      (c) =>
        c.key !== titleKey &&
        /category|group|type|section/i.test(c.key) &&
        c.distinct >= 2 &&
        c.distinct <= rows.length / 2,
    )?.key;
  }
  if (groupKey) {
    const buckets = new Map();
    const grouped = valueKey ? rows.filter((r) => r.value !== null) : rows;
    grouped.forEach((r) => {
      const raw = r.raw[groupKey];
      const gk = isEmptyValue(raw) ? "__other" : String(raw).trim().toLowerCase();
      if (!buckets.has(gk)) {
        buckets.set(gk, {
          id: `${path}::${gk}`,
          label: isEmptyValue(raw) ? "Other" : displayCase(raw),
          rowIndexes: [],
          subtotal: 0,
          first: buckets.size,
        });
      }
      const b = buckets.get(gk);
      b.rowIndexes.push(rows.indexOf(r));
      b.subtotal += r.value || 0;
    });
    groups = [...buckets.values()]
      .sort((a, b) => b.subtotal - a.subtotal || a.first - b.first)
      .map((b) => {
        const single = b.rowIndexes.length === 1;
        if (single) {
          const r = rows[b.rowIndexes[0]];
          const label = sameLabel(r.title, b.label) ? b.label : `${b.label}: ${r.title.toLowerCase()}`;
          r.tags = r.tags.filter((t) => t.label.toLowerCase() !== b.label.toLowerCase());
          return { id: b.id, label, kind: "single", rowIndexes: b.rowIndexes, subtotal: b.subtotal, share: sum ? b.subtotal / sum : 0 };
        }
        b.rowIndexes.forEach((i) => {
          rows[i].groupId = b.id;
          rows[i].tags = rows[i].tags.filter((t) => t.label.toLowerCase() !== b.label.toLowerCase());
        });
        return { id: b.id, label: b.label, kind: "group", rowIndexes: b.rowIndexes, subtotal: b.subtotal, share: sum ? b.subtotal / sum : 0 };
      });
    if (valueKey) {
      const empties = rows.map((r, i) => (r.value === null ? i : -1)).filter((i) => i >= 0);
      if (empties.length) {
        const id = `${path}::__empty`;
        empties.forEach((i) => (rows[i].groupId = id));
        groups.push({
          id,
          label: spec.emptyGroupLabel || "Without a value",
          kind: "empty",
          rowIndexes: empties,
          subtotal: 0,
          share: 0,
        });
      }
    }
  }

  const counts = {
    total: rows.length,
    withValue: withValue.length,
    withoutValue: valueKey ? rows.length - withValue.length : 0,
  };
  const noun = (spec.title || humanize(key)).toLowerCase();

  return {
    kind: "table",
    path,
    label: spec.title || humanize(key),
    columns,
    visibleColumns,
    titleKey,
    subtitleKey,
    valueKey,
    valueLabel: spec.valueLabel || (valueKey ? humanize(valueKey) : undefined),
    groupNoun: spec.groupNoun || (groupKey ? humanize(groupKey).toLowerCase() : undefined),
    rows,
    groups,
    total,
    counts,
    searchPlaceholder: `Search ${noun}`,
    noun,
    pageSize: 50,
    meta: spec.meta
      ? spec.meta(counts.withValue, counts.withoutValue)
      : pluralize(rows.length, valueKey ? "row" : "entry", valueKey ? "rows" : "entries"),
  };
}

export const isTableArray = (v) =>
  Array.isArray(v) && v.length > 0 && v.every((x) => isPlainObject(x) || x === null) && v.some(isPlainObject);

export const isScalarArray = (v) =>
  Array.isArray(v) && v.every((x) => x === null || ["string", "number", "boolean"].includes(typeof x));

